import React, { useState, useEffect } from 'react';
import { AlertTriangle, Save, RotateCcw } from 'lucide-react';

export interface AlertThresholdConfig {
  // Staff thresholds
  penaltyPointsWarning: number;
  penaltyPointsCritical: number;
  cleanRateWarning: number;
  cleanRateCritical: number;
  
  // Client thresholds
  utilizationLowWarning: number;
  utilizationLowCritical: number;
  utilizationHighWarning: number;
  
  // Financial thresholds
  revenueLeakageWarning: number;
  revenueLeakageCritical: number;
  pendingClaimsWarning: number;
  
  // Time thresholds (in days)
  noServiceAlertDays: number;
  authExpirationWarningDays: number;
}

const DEFAULT_THRESHOLDS: AlertThresholdConfig = {
  penaltyPointsWarning: 3,
  penaltyPointsCritical: 5,
  cleanRateWarning: 85,
  cleanRateCritical: 70,
  utilizationLowWarning: 50,
  utilizationLowCritical: 30,
  utilizationHighWarning: 95,
  revenueLeakageWarning: 5,
  revenueLeakageCritical: 10,
  pendingClaimsWarning: 20,
  noServiceAlertDays: 7,
  authExpirationWarningDays: 30,
};

interface AlertThresholdsProps {
  config: AlertThresholdConfig;
  onSave: (config: AlertThresholdConfig) => void;
}

export function AlertThresholds({ config, onSave }: AlertThresholdsProps) {
  const [localConfig, setLocalConfig] = useState<AlertThresholdConfig>(config);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleChange = (key: keyof AlertThresholdConfig, value: number) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(localConfig);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalConfig(DEFAULT_THRESHOLDS);
    setHasChanges(true);
  };

  const ThresholdInput = ({ 
    label, 
    configKey, 
    suffix = '',
    min = 0,
    max = 100,
    step = 1,
  }: { 
    label: string; 
    configKey: keyof AlertThresholdConfig;
    suffix?: string;
    min?: number;
    max?: number;
    step?: number;
  }) => (
    <div className="flex items-center justify-between py-2 gap-4">
      <label className="text-sm text-slate-300 flex-1">{label}</label>
      <div className="flex items-center gap-2 flex-shrink-0">
        <input
          type="number"
          value={localConfig[configKey]}
          onChange={(e) => handleChange(configKey, Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-200 text-sm text-right"
        />
        <span className="text-xs text-slate-400 w-10 text-left">{suffix || ''}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          <h3 className="text-lg font-medium text-slate-200">Custom Alert Thresholds</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={`flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              hasChanges
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Staff Thresholds */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3 pb-2 border-b border-slate-700">
            Staff Performance
          </h4>
          <div className="space-y-1">
            <ThresholdInput 
              label="Penalty Points (Warning)" 
              configKey="penaltyPointsWarning" 
              max={10}
            />
            <ThresholdInput 
              label="Penalty Points (Critical)" 
              configKey="penaltyPointsCritical" 
              max={10}
            />
            <ThresholdInput 
              label="Clean Rate Warning Below" 
              configKey="cleanRateWarning" 
              suffix="%"
            />
            <ThresholdInput 
              label="Clean Rate Critical Below" 
              configKey="cleanRateCritical" 
              suffix="%"
            />
          </div>
        </div>

        {/* Client Thresholds */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3 pb-2 border-b border-slate-700">
            Client Utilization
          </h4>
          <div className="space-y-1">
            <ThresholdInput 
              label="Low Utilization Warning" 
              configKey="utilizationLowWarning" 
              suffix="%"
            />
            <ThresholdInput 
              label="Low Utilization Critical" 
              configKey="utilizationLowCritical" 
              suffix="%"
            />
            <ThresholdInput 
              label="High Utilization Warning" 
              configKey="utilizationHighWarning" 
              suffix="%"
            />
          </div>
        </div>

        {/* Financial Thresholds */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3 pb-2 border-b border-slate-700">
            Financial Alerts
          </h4>
          <div className="space-y-1">
            <ThresholdInput 
              label="Revenue Leakage Warning" 
              configKey="revenueLeakageWarning" 
              suffix="%"
            />
            <ThresholdInput 
              label="Revenue Leakage Critical" 
              configKey="revenueLeakageCritical" 
              suffix="%"
            />
            <ThresholdInput 
              label="Pending Claims Warning" 
              configKey="pendingClaimsWarning" 
              suffix="%"
            />
          </div>
        </div>

        {/* Time Thresholds */}
        <div className="bg-slate-800/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3 pb-2 border-b border-slate-700">
            Time-based Alerts
          </h4>
          <div className="space-y-1">
            <ThresholdInput 
              label="No Service Alert After" 
              configKey="noServiceAlertDays" 
              suffix="days"
              max={30}
            />
            <ThresholdInput 
              label="Auth Expiration Warning" 
              configKey="authExpirationWarningDays" 
              suffix="days"
              max={90}
            />
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        These thresholds determine when alerts are triggered. Lower values for "warning" and "critical" 
        thresholds will generate more alerts. Changes are saved to your account.
      </p>
    </div>
  );
}

export { DEFAULT_THRESHOLDS };
